import { useRef } from 'react';
import { DragHandleRenderProps } from '@components/sortable-list';
import { useDndContext } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { useDragHeightActions, useInitialHeight } from '@stores/drag-height-store';

export interface UseDragHandleProps {
  id: string;
  dragHandle?: DragHandleRenderProps;
}

export enum InsertPosition {
  Before = 'before',
  After = 'after',
}

export interface UseDragHandleReturn {
  attributes: DragHandleRenderProps['attributes'];
  listeners: DragHandleRenderProps['listeners'];
  setNodeRef: ((node: HTMLElement | null) => void) | undefined;
  isDragging: boolean;
  isSorting: boolean;
  over: { id: string | number } | null;
  index: number;
  insertPosition: InsertPosition | undefined;
  transform: { x: number; y: number; scaleX: number; scaleY: number } | null;
  transition: string | undefined;
  containerRef: React.RefObject<HTMLDivElement>;
  getInitialHeight: () => number | undefined;
}

/**
 * Custom hook to handle drag and drop functionality with backward compatibility.
 *
 * This hook supports both:
 * - New architecture: When dragHandle is provided from SortableList
 * - Legacy architecture: When dragHandle is not provided, falls back to useSortable
 *
 *
 */
export const useDragHandle = ({ id, dragHandle }: UseDragHandleProps): UseDragHandleReturn => {
  // Get active and over from DndContext to calculate insert position
  const dndContext = useDndContext();
  const initialHeight = useInitialHeight();
  const { setInitialHeight } = useDragHeightActions();
  const active = dndContext.active;
  const overFromContext = dndContext.over;

  // Legacy useSortable hook (for backward compatibility)
  const legacySortable = useSortable({
    id,
  });

  // Ref to measure the height of the component in its initial state
  const containerRef = useRef<HTMLDivElement>(null);

  // Use dragHandle if provided (new architecture), otherwise fall back to legacy useSortable
  const attributes = dragHandle?.attributes ?? legacySortable.attributes;
  const listeners = dragHandle?.listeners ?? legacySortable.listeners;

  // Only use setNodeRef from legacy useSortable (new architecture applies ref automatically)
  const setNodeRef = dragHandle ? undefined : legacySortable.setNodeRef;

  // Get isDragging and isSorting from dragHandle or legacySortable
  const isDragging = dragHandle?.isDragging ?? legacySortable.isDragging;
  const isSorting = legacySortable.isSorting;
  const over = legacySortable.over;
  const index = legacySortable.index;

  // Transform and transition are only used in legacy mode
  const transform = dragHandle ? null : legacySortable.transform;
  const transition = legacySortable.transition;

  // Calculate insert position based on over element and active item
  // We use the data from useSortable which gives us the index of this element
  // and compare it with the active element's data
  let insertPosition: InsertPosition | undefined;
  if (overFromContext?.id === id && active && active.id !== id && !dragHandle) {
    // Get the index of the active item from its sortable data
    const activeSortableData = active.data.current?.sortable as { index?: number } | undefined;
    const activeIndex = activeSortableData?.index;
    const overIndex = index;

    // Only show indicator if we have valid indices and position has changed
    if (activeIndex !== undefined && overIndex !== undefined && activeIndex !== overIndex) {
      // Determine if we should show before or after based on drag direction
      insertPosition = overIndex > activeIndex ? InsertPosition.After : InsertPosition.Before;
    }
  }

  const getInitialHeight = () => {
    let internalHeight = 0;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      internalHeight = rect.height;
      setInitialHeight(rect.height);
    }
    return internalHeight > 0 ? internalHeight : initialHeight;
  };

  return {
    attributes,
    listeners,
    setNodeRef,
    getInitialHeight,
    isDragging,
    isSorting,
    over,
    index,
    insertPosition,
    transform: transform ?? null,
    transition,
    containerRef,
  };
};
